/**
 * Copyright (c) 2025, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./PublicImpact.css";

// The impact page as a signed-out visitor sees it: the network's real numbers,
// an estimator anyone can try, and the personal report locked behind sign-up.
class PublicImpact extends Component {
  render() {
    const vm = this.props.vm;
    const m = vm.m;
    const pv = vm.pv;
    return (
      <div className="Marketplace-Public Marketplace-animIn">
        <div className="Marketplace-Public-hero">
          <div className="Marketplace-Public-heroCopy">
            <div className="Marketplace-Public-eyebrow">{m.pubEyebrow}</div>
            <h2 className="Marketplace-Public-headline">{m.pubHeadline}</h2>
            <p className="Marketplace-Public-sub">{m.pubSub}</p>
          </div>
          <div className="Marketplace-Public-stats">
            {pv.stats.map((st, i) => (
              <React.Fragment key={st.id}>
                {i > 0 && <div className="Marketplace-Public-statDivider" />}
                <div>
                  <div className="Marketplace-Public-statValue">{st.value}</div>
                  <div className="Marketplace-Public-statLabel">{st.label}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="Marketplace-Public-try">
          <div className="Marketplace-Public-sectionLabel">{m.pubTryEyebrow}</div>
          <h3 className="Marketplace-Public-tryTitle">{m.pubTryTitle}</h3>
          <p className="Marketplace-Public-trySub">{m.pubTrySub}</p>
          <div className="Marketplace-Public-tryGrid">
            <div className="Marketplace-Public-picker">
              <div className="Marketplace-Public-pickerLabel">{m.pubICouldGive}</div>
              <div className="Marketplace-Public-pickerAmount">
                <span className="Marketplace-Public-currency">HK$</span>
                <span className="Marketplace-Public-amount">{pv.amountLabel.replace("HK$", "")}</span>
              </div>
              <div className="Marketplace-Public-presets">
                {pv.presets.map((p) => (
                  <div
                    className="Marketplace-Public-preset"
                    style={{ background: p.bg, color: p.fg, borderColor: p.border }}
                    onClick={p.pick}
                    key={p.id}
                  >
                    {p.label}
                  </div>
                ))}
              </div>
              <div className="Marketplace-Public-pickerFoot">{pv.overAYear}</div>
            </div>
            <div className="Marketplace-Public-rows">
              {pv.rows.map((r) => (
                <div className="Marketplace-Public-row" key={r.id}>
                  <div className="Marketplace-Public-rowTop">
                    <div className="Marketplace-Public-rowMono" style={{ background: r.tint, color: r.color }}>
                      {r.logo ? (
                        <img src={r.logo} alt={r.name} className="Marketplace-Public-rowMonoImg" />
                      ) : (
                        r.mono
                      )}
                    </div>
                    <div className="Marketplace-Public-rowInfo">
                      <div className="Marketplace-Public-rowName">{r.name}</div>
                      <div className="Marketplace-Public-rowMeta">
                        {r.pct} · {r.amountLabel}
                      </div>
                    </div>
                  </div>
                  <div className="Marketplace-Public-rowBand" style={{ background: r.tint }} />
                  <div className="Marketplace-Public-rowBody">
                    <div className="Marketplace-Public-rowCount" style={{ color: r.onWhite }}>
                      {r.count}
                    </div>
                    <div className="Marketplace-Public-rowNoun">{r.noun}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="Marketplace-Public-lockedWrap">
          <div className="Marketplace-Public-locked">
            {/* A blurred skeleton of the real report, so what's behind sign-up is legible. */}
            <div className="Marketplace-Public-skeleton" aria-hidden="true">
              <div className="Marketplace-Public-skelHead">
                <div>
                  <div className="Marketplace-Public-skelLabel" />
                  <div className="Marketplace-Public-skelTotal" />
                </div>
                <div className="Marketplace-Public-skelKpis">
                  <div className="Marketplace-Public-skelKpi" />
                  <div className="Marketplace-Public-skelKpi" />
                  <div className="Marketplace-Public-skelKpi" />
                </div>
              </div>
              <div className="Marketplace-Public-skelBar">
                <div style={{ width: "40%", background: "#2b91d6" }} />
                <div style={{ width: "35%", background: "#45b58a" }} />
                <div style={{ width: "25%", background: "#41b2fd" }} />
              </div>
              <div className="Marketplace-Public-skelRows">
                {["#e6f5ff", "#e8f7f1", "#e9f6ff"].map((tint) => (
                  <div className="Marketplace-Public-skelRow" key={tint}>
                    <div className="Marketplace-Public-skelMono" style={{ background: tint }} />
                    <div className="Marketplace-Public-skelLines">
                      <div className="Marketplace-Public-skelLine" />
                      <div className="Marketplace-Public-skelLine Marketplace-Public-skelLine--short" />
                    </div>
                    <div className="Marketplace-Public-skelAmount" />
                  </div>
                ))}
              </div>
            </div>
            <div className="Marketplace-Public-lockOverlay">
              <div className="Marketplace-Public-lockInner">
                <div className="Marketplace-Public-lockIcon">🔒</div>
                <div className="Marketplace-Public-lockTitle">{m.pubLockedTitle}</div>
                <div className="Marketplace-Public-lockBody">{m.pubLockedBody}</div>
                <div className="Marketplace-Public-lockBtns">
                  <div className="Marketplace-Public-btnPrimary" onClick={vm.goSignUp}>
                    {m.pubCreateAccount}
                  </div>
                  <div className="Marketplace-Public-btnGhost" onClick={vm.goLogin}>
                    {m.pubSignIn}
                  </div>
                </div>
                <div className="Marketplace-Public-lockNote">{m.pubLockedNote}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="Marketplace-Public-funded">
          <div className="Marketplace-Public-sectionLabel">{m.pubFundedTitle}</div>
          <div className="Marketplace-Public-fundedGrid">
            {pv.funded.map((f) => (
              <div className="Marketplace-Public-fundedTile" key={f.id}>
                <div className="Marketplace-Public-fundedValue" style={{ color: f.color }}>
                  {f.value}
                </div>
                <div className="Marketplace-Public-fundedLabel">{f.label}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="Marketplace-Public-ctaWrap">
          <div className="Marketplace-Public-cta">
            <div className="Marketplace-Public-ctaCopy">
              <div className="Marketplace-Public-ctaTitle">{m.pubCtaTitle}</div>
              <div className="Marketplace-Public-ctaBody">{m.pubCtaBody}</div>
            </div>
            <div className="Marketplace-Public-ctaBtns">
              <div className="Marketplace-Public-ctaSolid" onClick={vm.goSignUp}>
                {m.pubCreateAccount}
              </div>
              <div className="Marketplace-Public-ctaGhost" onClick={vm.goBrowse}>
                {m.pubCtaBrowse}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

PublicImpact.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default PublicImpact;
